import React, { useState, useEffect } from 'react';
import { Input, Button, List, Drawer, message, Spin } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import styles from './Chatbot.module.css';
import log from 'loglevel';
import { useAppSelector } from '../../hooks';
import { canUseAIAssistant, incrementAIAssistantUsage, getAIAssistantUsageToday, getAIAssistantMaxUsage, isExceededMaxInputTokens } from '../../utils/aiAssistantUsage';

interface Message {
  type: 'user' | 'bot';
  content: string;
}

interface ChatbotProps {
  open: boolean;
  onClose: () => void;
}

// DeepSeek-R1-671B-4K
// const DEEPSEEK_API_URL = "https://infer-modelarts-cn-southwest-2.modelarts-infer.com/v1/infers/c3cfa9e2-40c9-485f-a747-caae405296ef/v1/chat/completions"
// DeepSeek-R1-671B-32K
const DEEPSEEK_API_URL = "https://infer-modelarts-cn-southwest-2.modelarts-infer.com/v1/infers/952e4f88-ef93-4398-ae8d-af37f63f0d8e/v1/chat/completions"
// DeepSeek-V3-671B-4K
// const DEEPSEEK_API_URL = "https://infer-modelarts-cn-southwest-2.modelarts-infer.com/v1/infers/f354eacc-a2c5-43b4-a785-e5aadca988b3/v1/chat/completions"
const MODEL_NAME = 'DeepSeek-R1';
// const MODEL_NAME = 'DeepSeek-V3';
const MAX_TOKENS = 8192;

const Chatbot: React.FC<ChatbotProps> = ({ open, onClose }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const { memberType } = useAppSelector((state) => state.user);
  const [usageCount, setUsageCount] = useState(0);
  const [maxUsage, setMaxUsage] = useState(0);

  useEffect(() => {
    if (open) {
      const fetchUsageData = async () => {
        const currentUsage = await getAIAssistantUsageToday();
        const maxAllowedUsage = await getAIAssistantMaxUsage(memberType);
        setUsageCount(currentUsage);
        setMaxUsage(maxAllowedUsage);
      };
      
      fetchUsageData();
    }
  }, [open, memberType]);

  
  const estimateTokenCount = (text: string): number => {
    // 中文字符大约是1个token对应1个字符
    // 英文单词大约是1个token对应4-5个字符
    const chineseCharCount = (text.match(/[\u4e00-\u9fa5]/g) || []).length;
    const nonChineseCharCount = text.length - chineseCharCount;
    
    // 中文字符按1:1计算，英文和其他字符按4:1计算
    return chineseCharCount + Math.ceil(nonChineseCharCount / 4);
  };

  const handleSend = async () => {
    if (!inputValue.trim()) {
      return;
    }

    const estimatedTokens = estimateTokenCount(inputValue);
    if (isExceededMaxInputTokens(memberType, estimatedTokens)) {
      message.error('消息太长，请缩短后重试');
      return;
    }

    // 检查用户是否可以使用AI助手
    const canUse = await canUseAIAssistant(memberType);
    if (!canUse) {
      message.error(`您今日的AI助教使用次数已达上限（${maxUsage}次/天）`);
      return;
    }

    const userMessage: Message = {
      type: 'user',
      content: inputValue,
    };

    const processingMessage: Message = {
      type: 'bot',
      content: '思考中...',
    };

    setMessages([...messages, userMessage, processingMessage]);
    setInputValue('');
    setLoading(true);

    try {
      // 增加使用次数
      const newCount = await incrementAIAssistantUsage();
      setUsageCount(newCount);

      const response = await fetch(DEEPSEEK_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_DEEPSEEK_API_KEY}`
        },
        body: JSON.stringify({
          model: MODEL_NAME,
          max_tokens: MAX_TOKENS,
          messages: [
            { role: "system", content: "You are a helpful assistant." },
            ...messages.map(msg => ({
              role: msg.type === 'user' ? 'user' : 'assistant',
              content: msg.content
            })),
            { role: "user", content: userMessage.content }
          ],
          stream: false,
          temperature: 0.7
        }),
      });

      if (!response.ok) {
        log.error('网络请求失败:', response);
        throw new Error('网络请求失败');
      }

      const data = await response.json();
      const botMessage: Message = {
        type: 'bot',
        content: data.choices[0].message.content,
      };
      
      setMessages(prevMessages => [...prevMessages.slice(0, -1), botMessage]);
    } catch (error) {
      log.error('API 调用错误:', error);
      message.error('发送消息失败，请重试');
      setMessages(prevMessages => prevMessages.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title={`AI 助教 (今日已使用 ${usageCount}/${maxUsage} 次)`}
      placement="right"
      onClose={onClose}
      open={open}
      width={window.innerWidth < 768 ? '100%' : 400}
      className={styles.chatDrawer}
    >
      <div className={styles.chatContainer}>
        <List
          className={styles.messageList}
          itemLayout="horizontal"
          dataSource={messages}
          locale={{ emptyText: '我是你的AI助教，有问题随时问我哦' }}
          renderItem={(msg) => (
            <List.Item className={msg.type === 'user' ? styles.userMessage : styles.botMessage}>
              <List.Item.Meta
                description={msg.content}
              />
            </List.Item>
          )}
        />
        <div className={styles.inputContainer}>
          <Input.TextArea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder={usageCount < maxUsage ? "请输入您的问题..." : "今日使用次数已达上限"}
            disabled={loading || usageCount >= maxUsage}
            autoSize={{ minRows: 1, maxRows: 6 }}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSend}
            disabled={usageCount >= maxUsage}
          >
            发送
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default Chatbot; 